import { Listbox, Transition } from '@headlessui/react'
import React, { ReactElement, useEffect, useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faAngleDown } from '@fortawesome/pro-light-svg-icons/faAngleDown'
import { faAngleUp } from '@fortawesome/pro-light-svg-icons/faAngleUp'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'
import clsx from 'clsx'

import { InputStyle } from '../Input/Input'
import { Text } from '../Text/Text'

const Options = Listbox.Options

export type ISelectOption<T> = {
  id?: string | number
  disabled?: boolean
  text?: string | React.ReactNode
  icon?: IconDefinition
  customIcon?: string
  divider?: boolean
  onClick?: () => void
  href?: string
  hrefTarget?: '_blank' | '_self'
  description?: string
  value: T
  render?: () => React.ReactNode
}

interface SelectProps<T> {
  unmount?: boolean
  value: ISelectOption<T>
  onChange(value: ISelectOption<T>): void
  disabled?: boolean
  options: Array<ISelectOption<T>>
  optionStyles?: string
  buttonStyles?: string
  button?: ReactElement
  className?: string
  placeholder?: React.ReactElement | string
  label?: React.ReactNode
  error?: React.ReactElement | string
  showErrorMessage?: boolean
  showDescription?: boolean
  id?: string
  testId?: string
}

export function Select<ValueType>({
  button,
  buttonStyles,
  className,
  disabled,
  error,
  id,
  label,
  onChange,
  options,
  optionStyles,
  placeholder,
  showDescription = false,
  showErrorMessage = false,
  testId,
  value
}: SelectProps<ValueType>) {
  const [currentOption, setCurrentOption] = useState(value)

  useEffect(() => {
    setCurrentOption(value)
  }, [value])

  const renderOption = (option: ISelectOption<ValueType>) => {
    if (option.render) {
      return option.render()
    }
    if (option.customIcon) {
      return (
        <div
          className={`bg-[image:var(--image-url)] bg-contain bg-left bg-no-repeat pl-8`}
          style={{ '--image-url': `url(${option.customIcon})` } as React.CSSProperties}
        >
          {option.text ? <Text>{option.text}</Text> : null}
          {option.description ? <Text className={SelectStyle.option.description}>{option.description}</Text> : null}
        </div>
      )
    }
    return (
      <>
        {option.text ? <Text>{option.text}</Text> : null}
        {option.description ? <Text className={SelectStyle.option.description}>{option.description}</Text> : null}
      </>
    )
  }

  return (
    <div className={clsx('relative', className)}>
      {label || (error && typeof error !== 'boolean' && showErrorMessage) ? (
        <div className='mb-2 flex items-start justify-between'>
          {label ? (
            <label className={clsx(SelectStyle.label._, disabled && SelectStyle.label.disabled)}>{label}</label>
          ) : null}
          {error && typeof error !== 'boolean' && showErrorMessage ? (
            <div className='ml-auto flex items-center'>
              <FontAwesomeIcon className='mr-2 text-end text-red-500' icon={faExclamationTriangle} />
              <Text as='span' className={clsx(InputStyle.label.error['default'])} theme='sm'>
                {error}
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className='flex w-full gap-2'>
        <Listbox as='div' className='w-full' disabled={disabled} onChange={onChange} value={currentOption}>
          {({ open }) => (
            <>
              <Listbox.Button
                className={clsx(
                  SelectStyle.button._,
                  buttonStyles,
                  disabled && SelectStyle.option.disabled,
                  error && SelectStyle.button.error,
                  open && SelectStyle.button.open
                )}
                data-testid={testId}
                id={id}
              >
                {currentOption?.icon ? (
                  <FontAwesomeIcon className={SelectStyle.option.icon} icon={currentOption.icon} />
                ) : null}
                {currentOption?.customIcon ? (
                  <div
                    className={clsx('bg-[image:var(--image-url)] bg-contain bg-left bg-no-repeat pl-8')}
                    style={{ '--image-url': `url(${currentOption.customIcon})` } as React.CSSProperties}
                  >
                    {currentOption.text ? <Text>{currentOption.text}</Text> : null}
                    {showDescription && currentOption.description ? (
                      <Text className={SelectStyle.option.description}>{currentOption.description}</Text>
                    ) : null}
                  </div>
                ) : currentOption ? (
                  <div className='truncate text-left'>
                    <Text className='mr-2'>{currentOption.text}</Text>
                    {showDescription && currentOption.description ? (
                      <Text className={SelectStyle.option.description}>{currentOption.description}</Text>
                    ) : null}
                  </div>
                ) : null}
                {!currentOption && placeholder ? <span className='mr-2 truncate'>{placeholder}</span> : null}
                <FontAwesomeIcon className='ml-auto' icon={open ? faAngleUp : faAngleDown} />
              </Listbox.Button>
              <Transition
                className={clsx(className, optionStyles, SelectStyle.options.container, `absolute z-50 text-left`)}
                enter='transition duration-100 ease-out'
                enterFrom='transform scale-95 opacity-0'
                enterTo='transform scale-100 opacity-100'
                leave='transition duration-75 ease-out'
                leaveFrom='transform scale-100 opacity-100'
                leaveTo='transform scale-95 opacity-0'
              >
                <Options className={clsx(SelectStyle.options._)}>
                  {options.map((option, index) => (
                    <Listbox.Option
                      disabled={option.disabled}
                      key={option.id || index}
                      onClick={() => setCurrentOption(option)}
                      value={option}
                    >
                      {({ active }) => (
                        <div
                          className={clsx(
                            active && SelectStyle.option.active,
                            SelectStyle.option._,
                            option.disabled && SelectStyle.option.disabled
                          )}
                        >
                          {renderOption(option)}
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Options>
              </Transition>
            </>
          )}
        </Listbox>
        {button}
      </div>
    </div>
  )
}

const SelectStyle = {
  _: '',
  button: {
    _: 'dropdown_container flex w-full border border-border-default cursor-pointer items-center font-body select-none md:whitespace-nowrap   px-4 py-3  hover:no-underline transition rounded-full font-medium text-text-default hover:bg-background-default-hover active:bg-background-default-pressed',

    error: 'border-gray-600 placeholder:text-gray-600 !bg-error-default border-error-alternative',
    open: 'border-transparent shadow-primary-outline-thick'
  },
  label: {
    _: 'flex mb-0',
    disabled: 'text-gray-500',
    error: 'block text-red-500'
  },
  option: {
    _: 'block whitespace-normal cursor-pointer font-body select-none relative outline-0 border border-transparent transition active:bg-background-default active:bg-background-default-pressed focus:outline-none hover:no-underline px-4 py-3',
    active: 'text-primary-default bg-background-default-hover',
    description: 'text-gray-600 active:bg-black active:text-gray-200',
    disabled: '!cursor-wait',
    icon: 'flex-0 mr-2'
  },
  options: {
    _: 'relative bg-background-default text-text-default border border-gray-600 rounded-md mt-2 shadow max-h-80 right-0 absolute py-2 min-w-50 overflow-y-auto',
    container: ''
  }
}
